import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Users, Video, Wallet, Flag, BarChart3, Settings, Coins, Crown, Tag, Receipt, Sparkles, Copyright, Mail, Tv } from "lucide-react";
import { CopyrightTab, DigestTab, TvAppsTab } from "@/components/AdminExtras";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";

const TABS = [
  { k: "analytics", label: "Analytics", icon: BarChart3 },
  { k: "users", label: "Users", icon: Users },
  { k: "videos", label: "Videos", icon: Video },
  { k: "aigen", label: "AI Generator", icon: Sparkles },
  { k: "rules", label: "Reward Rules", icon: Settings },
  { k: "packages", label: "Coin Packages", icon: Coins },
  { k: "plans", label: "Membership Plans", icon: Crown },
  { k: "cats", label: "Categories", icon: Tag },
  { k: "txns", label: "Transactions", icon: Receipt },
  { k: "payouts", label: "Payouts", icon: Wallet },
  { k: "reports", label: "Reports", icon: Flag },
  { k: "copyright", label: "Copyright", icon: Copyright },
  { k: "digest", label: "Weekly Digest", icon: Mail },
  { k: "tvapps", label: "TV Apps", icon: Tv },
];

export default function Admin() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("analytics");
  useEffect(() => { if (ready && (!user || user.role !== "admin")) nav("/"); }, [ready, user, nav]);
  if (!user || user.role !== "admin") return null;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-6">
      <h1 className="h-font text-3xl font-extrabold flex items-center gap-2"><span className="text-[#FF2E63]">Admin</span> Dashboard</h1>
      <div className="flex gap-2 overflow-x-auto jx-scroll-x mt-4">
        {TABS.map(t=>(
          <button key={t.k} onClick={()=>setTab(t.k)} data-testid={`admin-tab-${t.k}`}
            className={`chip jx-btn shrink-0 px-4 h-10 rounded-full text-sm font-semibold inline-flex items-center gap-2 ${tab===t.k?'chip-active':''}`}>
            <t.icon size={14}/> {t.label}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "analytics" && <Analytics/>}
        {tab === "users" && <UsersTab/>}
        {tab === "videos" && <VideosTab/>}
        {tab === "aigen" && <AiGenTab/>}
        {tab === "rules" && <RulesTab/>}
        {tab === "packages" && <PackagesTab/>}
        {tab === "plans" && <PlansTab/>}
        {tab === "cats" && <CatsTab/>}
        {tab === "txns" && <TxnsTab/>}
        {tab === "payouts" && <PayoutsTab/>}
        {tab === "reports" && <ReportsTab/>}
        {tab === "copyright" && <CopyrightTab/>}
        {tab === "digest" && <DigestTab/>}
        {tab === "tvapps" && <TvAppsTab/>}
      </div>
    </div>
  );
}

function Card({ label, val, accent }) {
  return (
    <div className="rounded-2xl border border-white/10 p-5 bg-white/5">
      <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
      <div className="h-font text-3xl font-extrabold mt-1" style={accent?{color:accent}:{}}>{val?.toLocaleString?.() ?? val}</div>
    </div>
  );
}

function Analytics() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then(r=>setA(r.data)); }, []);
  if (!a) return <div className="text-white/60">Loading…</div>;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Users" val={a.total_users}/>
        <Card label="Videos" val={a.total_videos}/>
        <Card label="Shorts" val={a.total_shorts}/>
        <Card label="Total views" val={a.total_views}/>
        <Card label="Coins issued" val={Math.floor(a.coins_issued)} accent="#FFD700"/>
        <Card label="Pending payouts" val={a.pending_payouts} accent="#FF2E63"/>
      </div>
      <div className="rounded-2xl border border-white/10 p-4 mt-6 bg-white/5">
        <div className="h-font font-bold mb-2">Uploads (last 7 days)</div>
        <div className="h-56" style={{minWidth:0}}>
          <ResponsiveContainer width="100%" height="100%" minHeight={200}>
            <LineChart data={a.chart}>
              <XAxis dataKey="day" stroke="#666"/>
              <YAxis stroke="#666" allowDecimals={false}/>
              <Tooltip contentStyle={{background:"#121216",border:"1px solid #333"}}/>
              <Line type="monotone" dataKey="uploads" stroke="#08D9D6" strokeWidth={2} dot={{fill:"#08D9D6"}}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-6">
        <div className="rounded-2xl border border-white/10 p-5">
          <h3 className="h-font font-bold">Top creators</h3>
          <div className="mt-3 space-y-2">
            {a.top_creators.map(c=><div key={c.username} className="flex justify-between text-sm"><span>@{c.username}</span><span className="text-white/60">{c.views.toLocaleString()} views</span></div>)}
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 p-5">
          <h3 className="h-font font-bold">Top videos</h3>
          <div className="mt-3 space-y-2">
            {a.top_videos.map(v=><div key={v.id} className="flex justify-between text-sm"><span className="truncate">{v.title}</span><span className="text-white/60">{v.views.toLocaleString()}</span></div>)}
          </div>
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]); const [q, setQ] = useState("");
  const reload = () => api.get("/admin/users").then(r=>setUsers(r.data));
  useEffect(() => { reload(); }, []);
  const filtered = users.filter(u => u.email.includes(q) || u.username.includes(q));
  const toggle = async (id) => { await api.post(`/admin/users/${id}/suspend`); reload(); };
  const adjust = async (id) => {
    const d = prompt("Delta coins (+/-):"); if (!d) return;
    const reason = prompt("Reason:") || "Admin adjust";
    try { await api.post("/admin/users/coins", { user_id:id, delta:Number(d), reason }); reload(); toast.success("Adjusted"); }
    catch(e){toast.error(apiErr(e));}
  };
  return (
    <div>
      <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search users"
             className="w-full h-11 rounded-xl bg-white/5 border border-white/10 px-4 focus:outline-none focus:border-[#08D9D6]"/>
      <div className="mt-4 rounded-2xl border border-white/10 divide-y divide-white/5">
        {filtered.map(u=>(
          <div key={u.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
            <div className="flex-1 min-w-[200px]">
              <div className="font-semibold">@{u.username} <span className="text-white/50 text-xs">({u.role})</span></div>
              <div className="text-xs text-white/50">{u.email}</div>
            </div>
            <span className="text-[#FFD700] text-xs">{Math.floor(u.coins)} coins</span>
            <button onClick={()=>adjust(u.id)} className="chip jx-btn px-3 h-8 rounded-full text-xs">Adjust</button>
            <button onClick={()=>toggle(u.id)} className={`jx-btn px-3 h-8 rounded-full text-xs font-semibold ${u.suspended?'bg-[#FF2E63]':'bg-white/10 hover:bg-white/20'}`}>{u.suspended?'Unsuspend':'Suspend'}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function VideosTab() {
  const [vids, setVids] = useState([]);
  const reload = () => api.get("/admin/videos").then(r=>setVids(r.data));
  useEffect(() => { reload(); }, []);
  const hide = async id => { await api.post(`/admin/videos/${id}/hide`); reload(); };
  const feat = async id => { await api.post(`/admin/videos/${id}/feature`); reload(); };
  const promo = async id => { await api.post(`/admin/videos/${id}/promote`); reload(); };
  const del = async id => { if(!window.confirm("Delete?")) return; await api.delete(`/admin/videos/${id}`); reload(); };
  return (
    <div className="rounded-2xl border border-white/10 divide-y divide-white/5">
      {vids.map(v=>(
        <div key={v.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
          <div className="flex-1 min-w-[220px]">
            <div className="font-semibold truncate">{v.title} {v.is_paid && <span className="text-[#FFD700] text-xs">· {v.price_coins}c</span>}</div>
            <div className="text-xs text-white/50">@{v.creator?.username} · {v.format} · {v.views} views · {v.hidden?"HIDDEN":"visible"}</div>
          </div>
          <button onClick={()=>feat(v.id)} className={`chip jx-btn px-3 h-8 rounded-full text-xs ${v.featured?'chip-active':''}`}>Feature</button>
          <button onClick={()=>promo(v.id)} className={`chip jx-btn px-3 h-8 rounded-full text-xs ${v.is_promoted?'chip-active':''}`}>Promote</button>
          <button onClick={()=>hide(v.id)} className="chip jx-btn px-3 h-8 rounded-full text-xs">{v.hidden?'Show':'Hide'}</button>
          <button onClick={()=>del(v.id)} className="jx-btn px-3 h-8 rounded-full text-xs bg-[#FF2E63]/20 text-[#FF2E63]">Delete</button>
        </div>
      ))}
    </div>
  );
}

function AiGenTab() {
  const [mode, setMode] = useState("single"); // single or multi
  const [prompt, setPrompt] = useState("A cinematic, colorful, funny 3D cartoon: a man in a shirt that says JIXXEO sits on his couch eating pizza. Suddenly his refrigerator comes alive and chases him around the house. Expressive characters, dialogue, whimsical background music, cartoon sound effects, warm sunset lighting, vivid colors, playful animation.");
  const [shots, setShots] = useState([
    "Wide cinematic shot: a man in a bright shirt that says JIXXEO sits on his cozy living-room couch happily eating a slice of pizza. Warm sunset light streams through the window. Colorful 3D cartoon style, playful music.",
    "Medium shot: the refrigerator behind him suddenly wakes up with huge cartoon eyes and a mischievous grin. Comedic sound effects, dramatic zoom.",
    "Wide chase shot: the fridge stomps out of the kitchen and chases the panicked man around the house, pizza flying everywhere. Slapstick animation, upbeat funny music.",
    "Close-up: the man skids into the frame breathless, hides behind a giant plant, whispers 'why me?!' while the fridge zooms past. Cartoon dust cloud, comedic timing."
  ]);
  const [title, setTitle] = useState("Fridge Chase — Jixxeo Original");
  const [duration, setDuration] = useState(12);
  const [size, setSize] = useState("1280x720");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!job || job.status !== "processing") return;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/ai/video/${job.job_id}`);
        setJob(j => ({ ...j, ...data }));
        if (data.status !== "processing") clearInterval(iv);
      } catch {}
    }, 6000);
    return () => clearInterval(iv);
  }, [job?.job_id, job?.status]);

  const generateSingle = async () => {
    setBusy(true); setJob(null);
    try { const { data } = await api.post("/ai/video", { prompt, duration, size }); setJob({ ...data, prompt, kind: "single" }); toast.success("Generation started. 3–8 minutes."); }
    catch(e){ toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  const generateMulti = async () => {
    setBusy(true); setJob(null);
    try {
      const cleanShots = shots.map(s=>s.trim()).filter(Boolean);
      if (cleanShots.length < 2) { toast.error("Need at least 2 shots"); setBusy(false); return; }
      const { data } = await api.post("/ai/multishot", { prompts: cleanShots, duration_per_shot: duration, size, title });
      setJob({ ...data, kind: "multi", title });
      toast.success(`Generating ${data.total_shots} shots. Approx ${data.total_shots * 5} minutes.`);
    } catch(e){ toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <button onClick={()=>setMode("single")} data-testid="ai-mode-single"
                className={`chip jx-btn px-4 h-10 rounded-full text-sm font-semibold ${mode==="single"?'chip-active':''}`}>Single shot</button>
        <button onClick={()=>setMode("multi")} data-testid="ai-mode-multi"
                className={`chip jx-btn px-4 h-10 rounded-full text-sm font-semibold ${mode==="multi"?'chip-active':''}`}>Multi-shot film</button>
      </div>

      <div className="rounded-2xl border border-white/10 p-5 bg-gradient-to-br from-[#08D9D6]/10 to-[#FF2E63]/5">
        <h3 className="h-font text-xl font-bold flex items-center gap-2"><Sparkles size={18} className="text-[#08D9D6]"/>
          {mode==="single" ? "Generate video with Sora 2" : "Multi-shot AI film"}
        </h3>
        <p className="text-xs text-white/60 mt-1">
          {mode==="single" ? "Sora 2 supports 4, 8, or 12 second clips. Auto-published to Jixxeo."
                           : "Compose a longer story from multiple Sora 2 clips. Each shot runs through Sora sequentially and is auto-added to a public playlist."}
        </p>

        {mode==="single" ? (
          <textarea value={prompt} onChange={e=>setPrompt(e.target.value)} rows={5} data-testid="ai-prompt"
                    className="mt-4 w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-[#08D9D6]"/>
        ) : (
          <div className="mt-4 space-y-2">
            <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Film title" data-testid="ai-multi-title"
                   className="w-full h-11 rounded-xl bg-white/5 border border-white/10 px-3 text-sm focus:outline-none focus:border-[#08D9D6]"/>
            {shots.map((s,i)=>(
              <div key={i} className="flex gap-2">
                <span className="mt-2 text-xs text-white/40 shrink-0">#{i+1}</span>
                <textarea value={s} onChange={e=>setShots(l=>l.map((x,idx)=>idx===i?e.target.value:x))} rows={2} data-testid={`ai-shot-${i}`}
                          className="flex-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm"/>
                {shots.length > 2 && <button onClick={()=>setShots(l=>l.filter((_,idx)=>idx!==i))} className="jx-btn h-9 px-2 rounded-lg bg-[#FF2E63]/20 text-[#FF2E63] text-xs">×</button>}
              </div>
            ))}
            {shots.length < 6 && <button onClick={()=>setShots(l=>[...l, ""])} data-testid="ai-add-shot" className="chip jx-btn h-9 px-3 rounded-full text-xs font-semibold">+ Add shot ({shots.length}/6)</button>}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mt-3">
          <label className="text-xs text-white/60">Duration{mode==="multi" && " / shot"}
            <select value={duration} onChange={e=>setDuration(Number(e.target.value))} data-testid="ai-duration"
                    className="mt-1 w-full h-11 rounded-xl bg-white/5 border border-white/10 px-3">
              <option className="bg-black" value={4}>4s (fastest)</option>
              <option className="bg-black" value={8}>8s</option>
              <option className="bg-black" value={12}>12s (longest supported)</option>
            </select>
          </label>
          <label className="text-xs text-white/60">Resolution
            <select value={size} onChange={e=>setSize(e.target.value)} data-testid="ai-size"
                    className="mt-1 w-full h-11 rounded-xl bg-white/5 border border-white/10 px-3">
              <option className="bg-black" value="1280x720">Landscape 720p</option>
              <option className="bg-black" value="1792x1024">Widescreen</option>
              <option className="bg-black" value="1024x1792">Portrait (Shorts)</option>
              <option className="bg-black" value="1024x1024">Square</option>
            </select>
          </label>
        </div>
        <button onClick={mode==="single"?generateSingle:generateMulti} disabled={busy || (job && job.status==="processing")} data-testid="ai-generate-btn"
                className="jx-btn mt-5 h-11 px-6 rounded-xl bg-[#08D9D6] text-black font-bold hover:bg-[#06B2B0] disabled:opacity-50">
          {busy ? "Starting…" : (job?.status === "processing" ? "Generating…" : (mode==="single" ? "Generate" : `Generate ${shots.filter(s=>s.trim()).length}-shot film`))}
        </button>
        {mode==="multi" && <p className="text-[11px] text-white/50 mt-2">Total runtime ≈ {shots.filter(s=>s.trim()).length * duration}s across {shots.filter(s=>s.trim()).length} auto-linked clips.</p>}
      </div>

      {job && (
        <div className="mt-4 rounded-2xl border border-white/10 p-5">
          <div className="text-sm text-white/70">Job {job.job_id?.slice(0,8)} · <span className="font-bold">{job.status}</span>
            {job.kind==="multi" && job.total_shots && <span className="ml-2 text-white/50">({(job.progress_shots ?? 0)}/{job.total_shots} shots)</span>}
          </div>
          {job.status === "processing" && (
            <div className="mt-2 h-1 bg-white/10 rounded-full overflow-hidden">
              <div className="h-full bg-[#08D9D6] transition-all"
                   style={{width: job.kind==="multi" && job.total_shots ? `${((job.progress_shots||0)/job.total_shots)*100}%` : "50%"}}/>
            </div>
          )}
          {job.status === "completed" && job.kind !== "multi" && (
            <div className="mt-3">
              <video src={job.video_url} controls className="w-full max-w-lg rounded-xl bg-black"/>
              <a href={`/watch/${job.video_id}`} className="mt-2 inline-block text-sm font-semibold text-[#08D9D6]">Open on Jixxeo →</a>
            </div>
          )}
          {job.status === "completed" && job.kind === "multi" && (
            <div className="mt-3">
              <a href={`/playlists/${job.playlist_id}`} className="jx-btn inline-flex items-center gap-2 h-11 px-4 rounded-full bg-[#08D9D6] text-black font-bold">
                <Sparkles size={14}/> Watch {job.title || "the film"} →
              </a>
              <div className="text-xs text-white/50 mt-2">Playlist auto-created with {job.video_ids?.length ?? 0} linked shots.</div>
            </div>
          )}
          {job.status === "failed" && <div className="text-sm text-[#FF2E63] mt-2">Failed: {job.error}</div>}
        </div>
      )}
    </div>
  );
}

function RulesTab() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/admin/rules").then(x=>setR(x.data)); }, []);
  if (!r) return <div className="text-white/60">Loading…</div>;
  const save = async () => {
    try { const {data} = await api.put("/admin/rules", r); setR(data); toast.success("Rules saved"); }
    catch(e){toast.error(apiErr(e));}
  };
  const fields = [
    ["coins_per_view","Coins per view"],["coins_per_like","Coins per like"],["coins_per_upload","Coins per upload"],
    ["coins_per_follow","Coins per follow"],["coin_to_usd","Coin → USD"],
    ["min_cashout_coins","Min cashout coins ($5 = 5000 at $0.001)"],["max_cashout_coins","Max cashout coins"],
    ["daily_earn_limit","Daily earn limit"],["weekly_earn_limit","Weekly earn limit"],["monthly_earn_limit","Monthly earn limit"],
    ["platform_fee_pct","Platform fee % (sales/subs)"],["tip_platform_fee_pct","Tip platform fee %"],
    ["min_video_price_coins","Min video price (coins)"],["max_video_price_coins","Max video price (coins)"],
    ["signup_bonus_coins","Signup bonus (coins)"],
    ["boost_cost_coins","Boost cost (coins)"],["boost_duration_days","Boost duration (days)"],
    ["feature_cost_coins","Feature cost (coins)"],["feature_duration_days","Feature duration (days)"],
  ];
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {fields.map(([k,l])=>(
          <label key={k} className="block">
            <span className="text-xs text-white/60">{l}</span>
            <input type="number" step="0.001" value={r[k] ?? 0} onChange={e=>setR({...r, [k]: Number(e.target.value)})} data-testid={`rule-${k}`}
                   className="mt-1 w-full h-11 rounded-xl bg-white/5 border border-white/10 px-3 focus:outline-none focus:border-[#08D9D6]"/>
          </label>
        ))}
      </div>
      <button onClick={save} data-testid="save-rules-btn" className="jx-btn mt-5 h-11 px-6 rounded-xl bg-[#08D9D6] text-black font-bold hover:bg-[#06B2B0]">Save rules</button>
      <label className="mt-4 flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={!!r.hide_earnings_until_cashout} onChange={e=>setR({...r, hide_earnings_until_cashout: e.target.checked})} data-testid="rule-hide-earnings" className="w-4 h-4 accent-[#FFD700]"/>
        <span>Hide members' earnings until they reach the cashout threshold (show progress bar instead)</span>
      </label>
    </div>
  );
}

function PackagesTab() {
  const [pkgs, setPkgs] = useState([]);
  useEffect(()=>{ api.get("/admin/coin-packages").then(r=>setPkgs(r.data)); }, []);
  const set = (i, k, v) => setPkgs(list => list.map((p,idx)=>idx===i?{...p,[k]:v}:p));
  const add = () => setPkgs(l=>[...l,{id:`p${l.length+1}`, label:"New", coins:100, usd:0.99, highlight:false}]);
  const rm = i => setPkgs(l=>l.filter((_,idx)=>idx!==i));
  const save = async () => { try { await api.put("/admin/coin-packages", { packages: pkgs }); toast.success("Saved"); } catch(e){toast.error(apiErr(e));} };
  return (
    <div>
      <div className="space-y-2">
        {pkgs.map((p,i)=>(
          <div key={i} className="rounded-xl border border-white/10 p-3 grid grid-cols-2 md:grid-cols-6 gap-2 items-center">
            <input value={p.id} onChange={e=>set(i,'id',e.target.value)} placeholder="ID" className="h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm"/>
            <input value={p.label} onChange={e=>set(i,'label',e.target.value)} placeholder="Label" className="h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm"/>
            <input type="number" value={p.coins} onChange={e=>set(i,'coins',Number(e.target.value))} placeholder="Coins" className="h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm"/>
            <input type="number" step="0.01" value={p.usd} onChange={e=>set(i,'usd',Number(e.target.value))} placeholder="USD" className="h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm"/>
            <label className="text-xs inline-flex items-center gap-2"><input type="checkbox" checked={!!p.highlight} onChange={e=>set(i,'highlight',e.target.checked)}/> Highlight</label>
            <button onClick={()=>rm(i)} className="jx-btn h-10 px-3 rounded-lg bg-[#FF2E63]/20 text-[#FF2E63] text-sm">Remove</button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={add} className="chip jx-btn h-10 px-4 rounded-full text-sm font-semibold">+ Add package</button>
        <button onClick={save} data-testid="save-packages-btn" className="jx-btn h-10 px-4 rounded-full bg-[#08D9D6] text-black font-bold text-sm">Save all</button>
      </div>
    </div>
  );
}

function PlansTab() {
  const [plans, setPlans] = useState([]);
  useEffect(()=>{ api.get("/admin/membership-plans").then(r=>setPlans(r.data)); }, []);
  const set = (i, k, v) => setPlans(list => list.map((p,idx)=>idx===i?{...p,[k]:v}:p));
  const add = () => setPlans(l=>[...l,{id:`plan${l.length+1}`, name:"New Plan", coins_per_month:500, benefits:["Benefit 1"], active:true}]);
  const rm = i => setPlans(l=>l.filter((_,idx)=>idx!==i));
  const save = async () => { try { await api.put("/admin/membership-plans", { plans }); toast.success("Saved"); } catch(e){toast.error(apiErr(e));} };
  return (
    <div>
      <div className="space-y-3">
        {plans.map((p,i)=>(
          <div key={i} className="rounded-xl border border-white/10 p-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-center">
              <input value={p.id} onChange={e=>set(i,'id',e.target.value)} placeholder="ID" className="h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm"/>
              <input value={p.name} onChange={e=>set(i,'name',e.target.value)} placeholder="Name" className="h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm"/>
              <input type="number" value={p.coins_per_month} onChange={e=>set(i,'coins_per_month',Number(e.target.value))} placeholder="Coins/mo" className="h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm"/>
              <label className="text-xs inline-flex items-center gap-2"><input type="checkbox" checked={!!p.active} onChange={e=>set(i,'active',e.target.checked)}/> Active</label>
              <button onClick={()=>rm(i)} className="jx-btn h-10 px-3 rounded-lg bg-[#FF2E63]/20 text-[#FF2E63] text-sm">Remove</button>
            </div>
            <textarea value={(p.benefits||[]).join("\n")} onChange={e=>set(i,'benefits',e.target.value.split("\n"))} placeholder="Benefits (one per line)" rows={3}
                      className="mt-2 w-full rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm"/>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={add} className="chip jx-btn h-10 px-4 rounded-full text-sm font-semibold">+ Add plan</button>
        <button onClick={save} data-testid="save-plans-btn" className="jx-btn h-10 px-4 rounded-full bg-[#08D9D6] text-black font-bold text-sm">Save all</button>
      </div>
    </div>
  );
}

function CatsTab() {
  const [cats, setCats] = useState([]);
  useEffect(()=>{ api.get("/admin/categories").then(r=>setCats(r.data)); }, []);
  const save = async () => {
    try { await api.put("/admin/categories", { list: cats.filter(Boolean) }); toast.success("Saved"); }
    catch(e){toast.error(apiErr(e));}
  };
  return (
    <div>
      <div className="space-y-2">
        {cats.map((c,i)=>(
          <div key={i} className="flex gap-2 items-center">
            <input value={c} onChange={e=>setCats(l=>l.map((x,idx)=>idx===i?e.target.value:x))} className="flex-1 h-10 rounded-lg bg-white/5 border border-white/10 px-3 text-sm"/>
            {c!=="All" && <button onClick={()=>setCats(l=>l.filter((_,idx)=>idx!==i))} className="jx-btn h-10 px-3 rounded-lg bg-[#FF2E63]/20 text-[#FF2E63] text-sm">Remove</button>}
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={()=>setCats(l=>[...l,"New"])} className="chip jx-btn h-10 px-4 rounded-full text-sm font-semibold">+ Add</button>
        <button onClick={save} data-testid="save-cats-btn" className="jx-btn h-10 px-4 rounded-full bg-[#08D9D6] text-black font-bold text-sm">Save all</button>
      </div>
    </div>
  );
}

function TxnsTab() {
  const [rows, setRows] = useState([]);
  useEffect(()=>{ api.get("/admin/transactions").then(r=>setRows(r.data)); }, []);
  return (
    <div className="rounded-2xl border border-white/10 divide-y divide-white/5">
      {rows.length===0 && <div className="p-5 text-sm text-white/50">No transactions.</div>}
      {rows.map(r=>(
        <div key={`${r.type}-${r.id}`} className="grid grid-cols-2 md:grid-cols-5 gap-2 px-4 py-3 text-sm items-center">
          <div className="font-semibold capitalize">{r.type}</div>
          <div className="text-white/60 text-xs truncate">{r.user_id?.slice(0,8)} → {r.creator_id?.slice(0,8) || '-'}</div>
          <div className="text-[#FFD700] font-bold">{r.coins} coins</div>
          <div className="text-white/60 text-xs">{r.usd ? `$${r.usd}` : (r.creator_share ? `+${r.creator_share?.toFixed?.(1) || r.creator_share}` : '')}</div>
          <div className="text-white/50 text-xs">{new Date(r.created_at).toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
}

function PayoutsTab() {
  const [payouts, setPayouts] = useState([]);
  const reload = () => api.get("/admin/payouts").then(r=>setPayouts(r.data));
  useEffect(() => { reload(); }, []);
  const setStatus = async (id, s) => { await api.post(`/admin/payouts/${id}/status`, { status: s }); reload(); };
  return (
    <div className="rounded-2xl border border-white/10 divide-y divide-white/5">
      {payouts.length===0 && <div className="p-5 text-sm text-white/50">No payouts yet.</div>}
      {payouts.map(p=>(
        <div key={p.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
          <div className="flex-1 min-w-[200px]">
            <div className="font-semibold">@{p.username} — {p.coins} coins (~${p.usd_amount?.toFixed(2)})</div>
            <div className="text-xs text-white/50">{new Date(p.created_at).toLocaleString()} · {p.status}</div>
          </div>
          <button onClick={()=>setStatus(p.id,"paid")} className="jx-btn px-3 h-8 rounded-full text-xs bg-[#08D9D6] text-black font-bold">Mark paid</button>
          <button onClick={()=>setStatus(p.id,"rejected")} className="jx-btn px-3 h-8 rounded-full text-xs bg-[#FF2E63]/20 text-[#FF2E63]">Reject</button>
        </div>
      ))}
    </div>
  );
}

function ReportsTab() {
  const [rs, setRs] = useState([]);
  useEffect(()=>{ api.get("/admin/reports").then(r=>setRs(r.data)); }, []);
  return (
    <div className="rounded-2xl border border-white/10 divide-y divide-white/5">
      {rs.length===0 && <div className="p-5 text-sm text-white/50">No reports.</div>}
      {rs.map(r=>(
        <div key={r.id} className="px-4 py-3 text-sm">
          <div className="font-semibold">{r.target_type} · {r.target_id}</div>
          <div className="text-xs text-white/60">Reason: {r.reason}</div>
          <div className="text-xs text-white/50">{new Date(r.created_at).toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
}
